import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, imgUrl } from "../lib/api";
import { WorkerLayout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Play, Pause, LogOut as PunchOut, Sparkles, Coffee, Timer, Package, Download, X, ChevronLeft, ChevronRight, Images, CheckCircle2, Trash2, ListChecks } from "lucide-react";

const fmtMin = (m = 0) => {
  const h = Math.floor(m / 60), mm = m % 60;
  return `${h}h ${mm}m`;
};

export default function WorkerDashboard() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [generating, setGenerating] = useState(false);
  const [latest, setLatest] = useState(null);
  const [processing, setProcessing] = useState({});
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [viewingImageIdx, setViewingImageIdx] = useState(0);
  const [showClearModal, setShowClearModal] = useState(false);
  const [lastExportCount, setLastExportCount] = useState(0);
  const [clearing, setClearing] = useState(false);
  const [showProductivityModal, setShowProductivityModal] = useState(false);
  const [productivityData, setProductivityData] = useState(null);
  const [loadingProductivity, setLoadingProductivity] = useState(false);
  const [taskStatus, setTaskStatus] = useState(null);

  const load = useCallback(async () => {
    const [s, p, c, t] = await Promise.all([
      api.get("/attendance/me"),
      api.get("/products?mine=true&limit=20"),
      api.get("/admin/categories").catch(() => ({ data: [] })),
      api.get("/worker/tasks/status").catch(() => ({ data: null })),
    ]);
    setStatus(s.data);
    setProducts(p.data);
    setCategories(c.data || []);
    setTaskStatus(t.data);
    if (c.data?.length && !category) setCategory(c.data[0].name);
  }, [category]);

  useEffect(() => { load(); }, [load]);

  // heartbeat every 60s while active
  useEffect(() => {
    const h = setInterval(() => { api.post("/attendance/heartbeat").catch(() => {}); load(); }, 60000);
    return () => clearInterval(h);
  }, [load]);

  const call = async (path, ok) => {
    try { await api.post(path); toast.success(ok); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const handlePunchIn = async () => {
    try {
      await api.post("/attendance/punch-in");
      toast.success("Punched in — ready to work!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Punch in failed");
    }
  };

  const handlePunchOut = async () => {
    if (!status?.attendance) return;
    try {
      await api.post("/attendance/punch-out");
      toast.success("Punched out");
      // Load productivity data for the completed shift
      setLoadingProductivity(true);
      const r = await api.get(`/attendance/productivity/${status.attendance.id}`);
      setProductivityData(r.data);
      setShowProductivityModal(true);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Punch out failed");
    } finally {
      setLoadingProductivity(false);
    }
  };

  const generate = async () => {
    if (!status?.attendance) { toast.error("Punch in first."); return; }
    setGenerating(true); setLatest(null); setViewingImageIdx(0);
    try {
      const r = await api.post("/products/generate", { category: category || undefined });
      setLatest(r.data);
      const imgCount = [r.data.base_image_id, ...(r.data.additional_image_ids || [])].filter(Boolean).length;
      toast.success(`Generated: ${r.data.name}${imgCount > 1 ? ` · ${imgCount} images` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const handleDownload = async (imageId) => {
    try {
      const r = await api.get(`/images/${imageId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `product_${latest?.id}_image.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const handleRefine = async () => {
    if (!latest?.id) return;
    setProcessing((p) => ({ ...p, refine: true }));
    try {
      await api.post(`/products/${latest.id}/refine-image`);
      toast.success("Image refined successfully");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refine failed");
    } finally {
      setProcessing((p) => ({ ...p, refine: false }));
    }
  };

  const handleGenerateViews = async () => {
    if (!latest?.id) return;
    setProcessing((p) => ({ ...p, views: true }));
    try {
      await api.post(`/products/${latest.id}/generate-views`);
      toast.success("AI views generated successfully");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "View generation failed");
    } finally {
      setProcessing((p) => ({ ...p, views: false }));
    }
  };

  const handleSkip = async () => {
    if (!latest?.id) return;
    if (!confirm("Skip this image? It will be returned to the available pool.")) return;
    setProcessing((p) => ({ ...p, skip: true }));
    try {
      await api.post(`/products/${latest.id}/skip-image`);
      toast.success("Image skipped");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Skip failed");
    } finally {
      setProcessing((p) => ({ ...p, skip: false }));
    }
  };

  const handleComplete = async () => {
    if (!latest?.id) return;
    setProcessing((p) => ({ ...p, complete: true }));
    try {
      await api.post(`/products/${latest.id}/complete`);
      toast.success("Product completed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Complete failed");
    } finally {
      setProcessing((p) => ({ ...p, complete: false }));
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const r = await api.get("/products/export/csv", { responseType: "blob" });
      // Count rows (subtract header row)
      const text = await new Blob([r.data]).text();
      const rowCount = Math.max(0, text.trim().split("\n").length - 1);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      const dateStr = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `products_export_${dateStr}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setLastExportCount(rowCount);
      setShowClearModal(true);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleClearExported = async () => {
    setClearing(true);
    try {
      const r = await api.post("/products/export/clear");
      toast.success(`Done! ${r.data.cleared} products cleared. Fresh start for next export. ✓`);
      setShowClearModal(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setClearing(false);
    }
  };

  const toggleProductSelection = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const getExportStatusColor = (status) => {
    const colors = {
      pending: "bg-gray-500/20 text-gray-300",
      approved: "bg-green-500/20 text-green-300",
      exported: "bg-blue-500/20 text-blue-300"
    };
    return colors[status] || "bg-gray-500/20 text-gray-300";
  };

  const onBreak = !!status?.open_break;
  const punchedIn = !!status?.attendance;
  const idleWarn = status?.idle_in_seconds != null && status.idle_in_seconds < (status.warning_seconds || 300);

  return (
    <WorkerLayout>
      <div className="space-y-8">
        <section className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-overline text-[#D4AF37]">Worker studio</div>
            <h1 className="font-display text-4xl lg:text-5xl mt-2">Today's session</h1>
          </div>
          <div className="flex gap-3">
            {!punchedIn && (
              <Button onClick={handlePunchIn} data-testid="punch-in-btn"
                className="bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07] font-semibold"><Play className="w-4 h-4 mr-2"/>Punch in</Button>
            )}
            {punchedIn && !onBreak && (
              <Button onClick={() => call("/attendance/break/start", "Break started")} variant="outline" data-testid="break-start-btn" className="border-[#21362A]"><Coffee className="w-4 h-4 mr-2"/>Start break</Button>
            )}
            {punchedIn && onBreak && (
              <Button onClick={() => call("/attendance/break/end", "Break ended")} data-testid="break-end-btn" className="bg-[#097969] hover:bg-[#0a8a78]"><Pause className="w-4 h-4 mr-2"/>End break</Button>
            )}
            {punchedIn && (
              <Button onClick={handlePunchOut} data-testid="punch-out-btn" className="bg-[#E63946] hover:bg-[#d72e3b] text-white font-semibold"><PunchOut className="w-4 h-4 mr-2"/>Punch out</Button>
            )}
          </div>
        </section>

        {!punchedIn && status?.last_session && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-sm border border-[#21362A] bg-[#0C140F] p-4 flex items-center gap-4 text-sm"
            data-testid="last-session-banner"
          >
            <div className="w-8 h-8 rounded-full bg-[#132018] border border-[#21362A] flex items-center justify-center shrink-0">
              <Timer className="w-4 h-4 text-[#A1B4A8]" />
            </div>
            <div className="flex-1">
              <div className="text-xs text-[#A1B4A8] uppercase tracking-widest mb-0.5">Last session</div>
              <div className="text-white font-medium">
                {new Date(status.last_session.punch_in).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                {" · "}
                <span className="text-[#D4AF37]">
                  {new Date(status.last_session.punch_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
                {status.last_session.punch_out && (
                  <span className="text-[#A1B4A8]">
                    {" → "}
                    {new Date(status.last_session.punch_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </div>
              {status.last_session.total_minutes > 0 && (
                <div className="text-xs text-[#A1B4A8] mt-0.5">
                  Worked {fmtMin(status.last_session.total_minutes)}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {idleWarn && punchedIn && !onBreak && (
          <div className="rounded-sm border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-4 text-sm text-[#F0C84A]" data-testid="idle-warning">
            <Timer className="inline w-4 h-4 mr-2"/> You&apos;re nearing idle timeout. Generate a product or interact to stay active.
          </div>
        )}

        <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard label="Status" value={!punchedIn ? "Punched out" : onBreak ? "On break" : "Active"} data-testid="stat-status" />
          <StatCard label="Hours today" value={fmtMin(status?.total_today_minutes || 0)} data-testid="stat-hours" />
          <StatCard label="Products today" value={status?.products_today || 0} data-testid="stat-products-today" />
          <StatCard label="Idle timeout" value={`${status?.idle_timeout_minutes || 60}m`} data-testid="stat-idle-timeout" />
        </section>

        {punchedIn && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <Card
              className="bg-[#0C140F] border-[#D4AF37]/30 rounded-sm cursor-pointer hover:border-[#D4AF37]/60 transition-colors"
              onClick={() => navigate("/worker/tasks")}
              data-testid="dashboard-my-tasks-card"
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="space-y-0.5">
                    <div className="label-overline text-xs text-[#D4AF37]">Daily Focus</div>
                    <div className="font-display text-xl text-white">My Tasks</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {taskStatus?.has_tasks && (
                      <Badge
                        className={taskStatus?.all_confirmed
                          ? "bg-[#097969]/20 text-[#097969] border border-[#097969]/30"
                          : "bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30"}
                      >
                        {taskStatus?.all_confirmed
                          ? "✓ All confirmed"
                          : `${taskStatus?.confirmed ?? 0} / ${taskStatus?.total ?? 0} confirmed`}
                      </Badge>
                    )}
                    <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center shrink-0">
                      <ListChecks className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                  </div>
                </div>
                {taskStatus?.has_tasks ? (
                  <div className="space-y-1.5">
                    <div className="w-full bg-[#132018] rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-500 ${taskStatus?.all_confirmed ? "bg-[#097969]" : "bg-[#D4AF37]"}`}
                        style={{ width: `${taskStatus?.total > 0 ? Math.round(((taskStatus?.confirmed ?? 0) / taskStatus.total) * 100) : 0}%` }}
                      />
                    </div>
                    <div className="text-xs text-[#A1B4A8]">
                      {taskStatus?.all_confirmed
                        ? "Great work — all daily tasks done. Tap to review."
                        : `${taskStatus?.total - (taskStatus?.confirmed ?? 0)} task${(taskStatus?.total - (taskStatus?.confirmed ?? 0)) !== 1 ? "s" : ""} remaining · tap to view your list`}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-[#A1B4A8]">Tap to view and track today&apos;s tasks.</div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 bg-[#0C140F] border-[#21362A] rounded-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-display text-2xl">Generate product</CardTitle>
              <div className="w-56">
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="bg-[#132018] border-[#21362A]" data-testid="category-select"><SelectValue placeholder="Category"/></SelectTrigger>
                  <SelectContent className="bg-[#0C140F] border-[#21362A] text-white">
                    {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={generate} disabled={generating || !punchedIn} data-testid="generate-product-btn"
                className="w-full h-12 bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07] font-semibold tracking-wide">
                <Sparkles className="w-4 h-4 mr-2"/>{generating ? "Crafting product draft…" : "Generate product"}
              </Button>

              {latest && (() => {
                const allImages = [latest.base_image_id, ...(latest.additional_image_ids || [])].filter(Boolean);
                const currentImg = allImages[viewingImageIdx];
                const hasMultiple = allImages.length > 1;
                return (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="latest-product">
                    {/* Image gallery */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      {/* Main image viewer */}
                      <div className="sm:w-56 flex-shrink-0 space-y-2">
                        <div className="relative aspect-square bg-[#132018] border border-[#21362A] rounded-sm overflow-hidden">
                          {currentImg ? (
                            <img src={imgUrl(currentImg)} alt={`Product image ${viewingImageIdx + 1}`} className="w-full h-full object-cover"/>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[#A1B4A8] text-xs p-4 text-center">
                              <Images className="w-8 h-8 mb-2 opacity-40"/>
                            </div>
                          )}
                          {hasMultiple && (
                            <>
                              <button onClick={() => setViewingImageIdx(i => Math.max(0, i - 1))} disabled={viewingImageIdx === 0}
                                className="absolute left-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center disabled:opacity-30 hover:bg-black/80">
                                <ChevronLeft className="w-4 h-4"/>
                              </button>
                              <button onClick={() => setViewingImageIdx(i => Math.min(allImages.length - 1, i + 1))} disabled={viewingImageIdx === allImages.length - 1}
                                className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center disabled:opacity-30 hover:bg-black/80">
                                <ChevronRight className="w-4 h-4"/>
                              </button>
                              <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-xs bg-black/70 text-white">
                                {viewingImageIdx + 1} / {allImages.length}
                              </div>
                            </>
                          )}
                        </div>
                        {/* Thumbnails */}
                        {hasMultiple && (
                          <div className="flex gap-1 overflow-x-auto">
                            {allImages.map((id, i) => (
                              <button key={id} onClick={() => setViewingImageIdx(i)}
                                className={`flex-shrink-0 w-12 h-12 rounded border-2 overflow-hidden ${
                                  i === viewingImageIdx ? "border-[#D4AF37]" : "border-[#21362A] hover:border-[#A1B4A8]"
                                }`}>
                                <img src={imgUrl(id)} alt={`thumb ${i + 1}`} className="w-full h-full object-cover"/>
                              </button>
                            ))}
                          </div>
                        )}
                        {allImages.length > 0 && (
                          <div className="flex items-center gap-1 text-xs text-[#A1B4A8]">
                            <Images className="w-3 h-3"/>
                            <span>{allImages.length} image{allImages.length !== 1 ? "s" : ""} · {viewingImageIdx === 0 ? "Base image" : `View ${viewingImageIdx}`}</span>
                          </div>
                        )}
                      </div>

                      {/* Product details */}
                      <div className="flex-1 space-y-3 p-4 border border-[#21362A] rounded-sm bg-[#050A07]">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="label-overline text-xs">Product name</div>
                            <div className="font-display text-xl mt-0.5 leading-tight">{latest.name}</div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="label-overline text-xs">Price</div>
                            <div className="font-display text-2xl text-[#D4AF37]">${latest.final_price}</div>
                          </div>
                        </div>
                        <div className="text-sm text-[#A1B4A8] leading-relaxed">{latest.short_description}</div>
                        <div className="text-xs text-[#A1B4A8] leading-relaxed line-clamp-4">{latest.full_description}</div>
                        <div className="flex gap-1.5 flex-wrap">
                          {latest.tags?.map((t) => <Badge key={t} className="bg-[#132018] text-[#A1B4A8] border border-[#21362A] text-xs">{t}</Badge>)}
                        </div>
                        <div className="text-xs text-[#A1B4A8]">Sizes: {latest.sizes?.join(", ")}</div>
                        <div className="text-xs text-[#A1B4A8]">
                          SKU: {latest.sku} · {latest.category}
                        </div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {currentImg && (
                        <Button onClick={() => handleDownload(currentImg)} variant="outline" className="border-[#21362A] text-[#A1B4A8] text-xs">
                          <Download className="w-3 h-3 mr-1"/>Download
                        </Button>
                      )}
                      <Button onClick={() => navigate(`/products/${latest.id}`)} variant="outline" className="border-[#21362A] text-[#A1B4A8] text-xs">
                        View Details
                      </Button>
                      {latest.image_workflow_status === "assigned" && (
                        <>
                          <Button onClick={handleRefine} disabled={processing.refine} className="bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07] text-xs">
                            <Sparkles className="w-3 h-3 mr-1"/>{processing.refine ? "Refining..." : "Refine Image"}
                          </Button>
                          <Button onClick={handleGenerateViews} disabled={processing.views} className="bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07] text-xs">
                            <Images className="w-3 h-3 mr-1"/>{processing.views ? "Generating..." : "Create Views"}
                          </Button>
                          <Button onClick={handleSkip} disabled={processing.skip} variant="outline" className="border-[#21362A] text-[#A1B4A8] text-xs">
                            <X className="w-3 h-3 mr-1"/>{processing.skip ? "Skipping..." : "Skip"}
                          </Button>
                        </>
                      )}
                      <Button
                        onClick={handleComplete}
                        disabled={processing.complete || latest.image_workflow_status === "completed"}
                        className="bg-[#097969] hover:bg-[#0a8a78] col-span-2 sm:col-span-1 text-xs">
                        <CheckCircle2 className="w-3 h-3 mr-1"/>
                        {processing.complete ? "Saving..." : latest.image_workflow_status === "completed" ? "Saved ✓" : "Save Product Record"}
                      </Button>
                    </div>

                    {allImages.length > 1 && (
                      <div className="text-xs text-[#D4AF37]/70 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-sm px-3 py-2">
                        ✦ {allImages.length} product views available — all images are included in the CSV export automatically.
                      </div>
                    )}
                  </motion.div>
                );
              })()}

              {!latest && !generating && (
                <div className="text-center py-8 text-[#A1B4A8] text-sm">
                  <Images className="w-10 h-10 mx-auto mb-3 opacity-30"/>
                  Click &ldquo;Generate product&rdquo; to create a new product draft with auto-assigned images.
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-display text-2xl">Product Queue</CardTitle>
              <div className="flex gap-2">
                <Button onClick={handleExportCSV} disabled={exporting} size="sm" className="bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07]">
                  {exporting ? "Exporting..." : "Export CSV"}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="recent-products">
              {products.length === 0 && <div className="text-sm text-[#A1B4A8]">Nothing yet — generate your first product.</div>}
              {products.map((p) => (
                <div 
                  key={p.id} 
                  onClick={() => navigate(`/products/${p.id}`)}
                  className="flex items-center gap-3 p-2 rounded-sm border border-transparent hover:border-[#21362A] cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedProducts.has(p.id)}
                    onChange={(e) => { e.stopPropagation(); toggleProductSelection(p.id); }}
                    className="w-4 h-4"
                  />
                  <Package className="w-4 h-4 text-[#D4AF37]"/>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm">{p.name}</div>
                    <div className="text-xs text-[#A1B4A8] truncate">{p.category}</div>
                  </div>
                  <Badge className={getExportStatusColor(p.export_status || "pending")}>{p.export_status || "pending"}</Badge>
                  <div className="font-display text-[#D4AF37]">${p.final_price}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      </div>

      {/* ── Post-export confirmation modal ── */}
      <Dialog open={showClearModal} onOpenChange={setShowClearModal}>
        <DialogContent className="bg-[#0C140F] border-[#21362A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-[#D4AF37]" />
              CSV Exported Successfully
            </DialogTitle>
            <DialogDescription className="text-[#A1B4A8] text-sm mt-1">
              Your file with <strong className="text-white">{lastExportCount} product{lastExportCount !== 1 ? "s" : ""}</strong> has been downloaded.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-[#132018] border border-[#21362A] rounded-sm p-4 my-2 space-y-1">
            <p className="text-sm font-medium text-white">Did the import to your ecommerce site succeed?</p>
            <p className="text-xs text-[#A1B4A8]">
              If yes, we'll clear these products from the export queue so you start fresh next time.
              If no, keep them and re-export after fixing any issues.
            </p>
          </div>

          <div className="flex flex-col gap-2 pt-1">
            <Button
              onClick={handleClearExported}
              disabled={clearing}
              className="w-full bg-[#097969] hover:bg-[#0a8a78] text-white gap-2"
            >
              <Trash2 className="w-4 h-4" />
              {clearing ? "Clearing…" : "Yes, import was successful — clear the list"}
            </Button>
            <Button
              onClick={() => setShowClearModal(false)}
              variant="outline"
              className="w-full border-[#21362A] text-[#A1B4A8] hover:text-white"
            >
              No, keep for re-export
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Productivity summary modal after punch-out ── */}
      <Dialog open={showProductivityModal} onOpenChange={setShowProductivityModal}>
        <DialogContent className="bg-[#0C140F] border-[#21362A] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white flex items-center gap-2">
              <Timer className="w-6 h-6 text-[#D4AF37]" />
              Shift Summary
            </DialogTitle>
            <DialogDescription className="text-[#A1B4A8] text-sm mt-1">
              Your productivity for this work session
            </DialogDescription>
          </DialogHeader>

          {loadingProductivity ? (
            <div className="py-8 text-center text-[#A1B4A8]">Loading productivity data...</div>
          ) : productivityData ? (
            <div className="space-y-4 my-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#132018] border border-[#21362A] rounded-sm p-3">
                  <div className="label-overline text-xs">Total Shift Time</div>
                  <div className="font-display text-xl mt-1">{fmtMin(productivityData.total_shift_minutes)}</div>
                </div>
                <div className="bg-[#132018] border border-[#21362A] rounded-sm p-3">
                  <div className="label-overline text-xs">Work Time</div>
                  <div className="font-display text-xl mt-1 text-[#D4AF37]">{fmtMin(productivityData.work_minutes)}</div>
                </div>
                <div className="bg-[#132018] border border-[#21362A] rounded-sm p-3">
                  <div className="label-overline text-xs">Break Time</div>
                  <div className="font-display text-xl mt-1 text-[#A1B4A8]">{fmtMin(productivityData.break_minutes)}</div>
                </div>
                <div className="bg-[#132018] border border-[#21362A] rounded-sm p-3">
                  <div className="label-overline text-xs">Idle Time</div>
                  <div className="font-display text-xl mt-1 text-[#E63946]">{fmtMin(productivityData.idle_minutes)}</div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-[#132018] border border-[#21362A] rounded-sm p-3 text-center">
                  <div className="label-overline text-xs">Products Generated</div>
                  <div className="font-display text-2xl mt-1 text-[#D4AF37]">{productivityData.products_generated}</div>
                </div>
                <div className="bg-[#132018] border border-[#21362A] rounded-sm p-3 text-center">
                  <div className="label-overline text-xs">Items Exported</div>
                  <div className="font-display text-2xl mt-1 text-[#097969]">{productivityData.products_exported}</div>
                </div>
                <div className="bg-[#132018] border border-[#21362A] rounded-sm p-3 text-center">
                  <div className="label-overline text-xs">Products/Hour</div>
                  <div className="font-display text-2xl mt-1 text-white">{productivityData.products_per_hour}</div>
                </div>
              </div>

              {productivityData.breaks && productivityData.breaks.length > 0 && (
                <div className="bg-[#132018] border border-[#21362A] rounded-sm p-3">
                  <div className="label-overline text-xs mb-2">Breaks Taken</div>
                  <div className="space-y-1">
                    {productivityData.breaks.map((b, i) => (
                      <div key={b.id} className="text-xs text-[#A1B4A8] flex justify-between">
                        <span>Break #{i + 1}</span>
                        <span>{fmtMin(b.minutes)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-4 text-center text-[#A1B4A8]">No productivity data available</div>
          )}

          <Button
            onClick={() => setShowProductivityModal(false)}
            className="w-full bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07] font-semibold"
          >
            Close
          </Button>
        </DialogContent>
      </Dialog>

    </WorkerLayout>
  );
}

function StatCard({ label, value, ...rest }) {
  return (
    <Card className="bg-[#0C140F] border-[#21362A] rounded-sm" {...rest}>
      <CardContent className="p-5">
        <div className="label-overline">{label}</div>
        <div className="font-display text-3xl mt-2">{value}</div>
      </CardContent>
    </Card>
  );
}
